import { useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const PANEL =
  'max-w-[280px] whitespace-normal break-words rounded-xl bg-slate-900 px-3 py-2 ' +
  'text-[11px] leading-relaxed font-body text-slate-100 shadow-xl ring-1 ring-white/10';

function Body({ title, text }) {
  return (
    <>
      {title && <p className="font-heading text-[11px] font-semibold text-white mb-0.5">{title}</p>}
      <p>{text}</p>
    </>
  );
}

/**
 * Hover/focus explainer for a control that isn't self-explanatory.
 * Wraps the child in a span so the trigger works even for disabled buttons
 * (a disabled button emits no pointer events of its own).
 */
export function Hint({ text, title, side = 'top', align = 'center', className, children, testId }) {
  if (!text) return children;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex', className)}>{children}</span>
        </TooltipTrigger>
        <TooltipContent side={side} align={align} className={PANEL} data-testid={testId}>
          <Body title={title} text={text} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Standalone ⓘ for headings and longer explanations. Opens on hover AND on
 * tap, since Radix tooltips are hover-only and phones have no hover.
 */
export function InfoDot({ text, title, side = 'top', align = 'center', className, testId, label = 'What is this?' }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button type="button" aria-label={label} data-testid={testId}
            onClick={() => setOpen(o => !o)}
            className={cn(
              'inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-400',
              'hover:text-indigo-600 hover:bg-indigo-50 focus:outline-none focus-visible:ring-2',
              'focus-visible:ring-indigo-400 transition-colors align-middle flex-shrink-0',
              className,
            )}>
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} align={align} className={PANEL}>
          <Body title={title} text={text} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default Hint;
